import { Button } from '@mui/material';
import { Code } from '@ankr.com/ui';
import { Chain } from '@ankr.com/chains-list';
import { useCallback } from 'react';

import { AddNetworkButton } from 'modules/wallet/components/AddNetwork';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';
import { useIsXSDown } from 'uiKit/Theme/useTheme';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { hasPublicSubchains } from 'modules/chains/utils/hasPublicSubchains';
import { useAuthInstance } from 'modules/auth/hooks/useAuthInstance';

import { ChainDocsLink } from '../ChainDocsLink';
import { chainItemHeaderExtraContentTranslation } from './translation';
import { useChainItemHeaderExtraContentStyles } from './useChainItemHeaderStyles';
import { useChainItemHeaderExtraContent } from './useChainItemHeaderExtraContent';

interface IChainItemHeaderExtraContentProps {
  chain: Chain;
  hasMetamaskButton?: boolean;
  isCodeExampleHidden?: boolean;
  hasSelectorForMetamaskButton?: boolean;
  onOpenCodeExample?: () => void;
  onOpenUpgradeModal?: () => void;
}

export const ChainItemHeaderExtraContent = ({
  chain,
  hasMetamaskButton,
  hasSelectorForMetamaskButton,
  isCodeExampleHidden,
  onOpenCodeExample,
  onOpenUpgradeModal,
}: IChainItemHeaderExtraContentProps) => {
  const { isCodeExampleDisabled } = useChainItemHeaderExtraContent(
    chain,
    onOpenCodeExample,
  );

  const isMobile = useIsXSDown();

  const { keys, t } = useTranslation(chainItemHeaderExtraContentTranslation);

  const { classes } = useChainItemHeaderExtraContentStyles();

  const { isSignedIn } = useAuthInstance();
  const { isFreemium } = useAccountStatus();

  const isPublicBlocked = !isSignedIn && !hasPublicSubchains(chain);
  const isFreemiumBlocked = isSignedIn && isFreemium && chain.premiumOnly;
  const shouldOpenUpgradeModal = isPublicBlocked || isFreemiumBlocked;

  const handleClickMetamaskButton = useCallback(() => {
    if (shouldOpenUpgradeModal && onOpenUpgradeModal) {
      onOpenUpgradeModal();
    }
  }, [onOpenUpgradeModal, shouldOpenUpgradeModal]);

  return (
    <div className={classes.extraContent}>
      {!isCodeExampleHidden && (
        <Button
          onClick={onOpenCodeExample}
          variant="outlined"
          size="small"
          className={classes.codeExampleButton}
          disabled={isCodeExampleDisabled}
        >
          <Code />
          {t(keys.codeExample)}
        </Button>
      )}
      <ChainDocsLink
        id={chain.id}
        size="small"
        className={classes.docsLink}
        isTextHidden={isMobile}
      />
      {(hasMetamaskButton || hasSelectorForMetamaskButton) && (
        <AddNetworkButton
          className={classes.addNetworkButton}
          chain={chain}
          size="small"
          hasChainSelector
          onClick={
            shouldOpenUpgradeModal ? handleClickMetamaskButton : undefined
          }
        />
      )}
    </div>
  );
};
